import {
  Badge,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { COLORS } from "../../../../utils/Colors";
import { IconButton } from "../../../components/buttons/IconButton";
import {
  MdOutlineDeleteOutline,
  MdOutlineModeEditOutline,
} from "react-icons/md";
import { IoIosMore } from "react-icons/io";
import { Store, StoreStatus } from "../../../../entity/Store";

const options = [
  { code: "mg", label: "store.migration" },
  { code: "del", label: "store.deletePermanently" },
];

interface Props {
  stores: Store[];
  onEdit: (store: Store) => void;
  onDelete: (storeId: string) => Promise<void>;
  onMigrate: (storeId: string) => Promise<void>;
  isShowStatus: boolean;
  onDeletePermanently: (storeId: string) => Promise<void>;
}

export const StoresTable: React.FC<Props> = ({
  stores,
  onEdit,
  onDelete,
  onMigrate,
  isShowStatus,
  onDeletePermanently,
}) => {
  const { t } = useTranslation();

  const handleSelect = (code: string, storeId: string) => {
    switch (code) {
      case "mg":
        onMigrate(storeId);
        break;
      case "del":
        onDeletePermanently(storeId);
        break;
      default:
        break;
    }
  };

  return (
    <TableContainer mt={6}>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>{t("store.name")}</Th>
            <Th>{t("store.email")}</Th>
            <Th>{t("store.phoneNumber")}</Th>
            <Th>{t("store.address")}</Th>
            <Th>{t("store.subdomain")}</Th>
            {isShowStatus && <Th>{t("store.status")}</Th>}
            <Th>{t("actions")}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {stores.map((store) => (
            <Tr
              color={COLORS.Text.Primary}
              key={store.id}
              _hover={{ bg: "gray.50" }}
            >
              <Td color={COLORS.Text.Primary} fontWeight="medium">
                {store.name}
              </Td>
              <Td color={COLORS.Text.Primary}>{store.email}</Td>
              <Td color={COLORS.Text.Primary}>{store.phoneNumber}</Td>
              <Td color={COLORS.Text.Primary}>{store.address}</Td>
              <Td>
                <Badge>{store.subdomain}</Badge>
              </Td>
              {isShowStatus && (
                <Td>
                  <Badge
                    colorScheme={
                      store.status === StoreStatus.ENABLED ? "green" : "red"
                    }
                    textTransform="capitalize"
                  >
                    {t(`store.${store.status?.toLowerCase()}`)}
                  </Badge>
                </Td>
              )}
              <Td>
                <HStack spacing={2}>
                  <IconButton label={t("edit")} onClick={() => onEdit(store)}>
                    <MdOutlineModeEditOutline size={20} />
                  </IconButton>
                  <IconButton
                    aria-label={t("delete")}
                    onClick={() => store.id && onDelete(store.id)}
                  >
                    <MdOutlineDeleteOutline
                      size={20}
                      color={COLORS.Palette.Red}
                    />
                  </IconButton>
                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<IoIosMore />}
                      textTransform="capitalize"
                    >
                      {t("actions")}
                    </MenuButton>
                    <MenuList>
                      {options.map((l) => (
                        <MenuItem
                          key={l.code}
                          onClick={() => handleSelect(l.code, store.id!!)}
                        >
                          {t(l.label)}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
